import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Grid
} from '@mui/material';
import {
  Search,
  Whatshot,
  Speed,
  Pause
} from '@mui/icons-material';
import DashboardService from '../../services/dashboard';
import DateInterval from '../../components/calendar/DateInterval';

const RotationAnalysis = ({ storeId }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rotationFilter, setRotationFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    date_from: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await DashboardService.getRotationAnalysis(storeId, {
        ...dateRange,
        limit: 100
      });
      setData(result);
      setFilteredData(result);
    } catch (err) {
      setError('Error al cargar el análisis de rotación');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [storeId, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let filtered = data;
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (rotationFilter !== 'all') {
      filtered = filtered.filter(item => item.rotation_category === rotationFilter);
    }
    setFilteredData(filtered);
  }, [searchTerm, rotationFilter, data]);

  const handleDateChange = (start, end) => {
    if (start && end) {
      setDateRange({
        date_from: start.format('YYYY-MM-DD'),
        date_to: end.format('YYYY-MM-DD')
      });
    }
  };

  const getRotationChip = (category) => {
    const configs = {
      fast: { label: 'Rápida', color: 'success', icon: <Whatshot /> },
      medium: { label: 'Media', color: 'warning', icon: <Speed /> },
      slow: { label: 'Lenta', color: 'error', icon: <Pause /> }
    };
    const config = configs[category] || configs.medium;
    return (
      <Chip 
        label={config.label}
        size="small"
        color={config.color}
        icon={config.icon}
      />
    );
  };

  // Calculate summary by category
  const summary = {
    fast: data.filter(p => p.rotation_category === 'fast').length,
    medium: data.filter(p => p.rotation_category === 'medium').length,
    slow: data.filter(p => p.rotation_category === 'slow').length
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Date Filter and Search */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <DateInterval
            startDate={dateRange.date_from}
            endDate={dateRange.date_to}
            onChange={handleDateChange}
          />
        </Box>
        <TextField
          placeholder="Buscar producto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'success.main', bgcolor: 'success.light', color: 'success.dark' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Whatshot />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Rotación Rápida
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {summary.fast}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'warning.main', bgcolor: 'warning.light', color: 'warning.dark' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Speed />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Rotación Media
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {summary.medium}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'error.main', bgcolor: 'error.light', color: 'error.dark' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Pause />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Rotación Lenta
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {summary.slow}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Rotation Filter */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={rotationFilter}
          exclusive
          onChange={(e, value) => value && setRotationFilter(value)}
          size="small"
        >
          <ToggleButton value="all">
            Todos ({data.length})
          </ToggleButton>
          <ToggleButton value="fast">
            Rápida ({summary.fast})
          </ToggleButton>
          <ToggleButton value="medium">
            Media ({summary.medium})
          </ToggleButton>
          <ToggleButton value="slow">
            Lenta ({summary.slow})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Rotation Table */}
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Análisis de Rotación de Inventario
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Velocidad de rotación y días para vender el inventario actual
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Comprado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Vendido</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Stock Actual</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Tasa Rotación</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Días p/ Vender</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Categoría</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Última Venta</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Días Sin Vender</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No hay datos disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((product) => (
                  <TableRow key={product.article_id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.sku} • {product.category_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{product.total_purchased}</TableCell>
                    <TableCell align="right">{product.total_sold}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={product.current_stock}
                        size="small"
                        color={product.current_stock < 10 ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: (product.rotation_rate || 0) > 2 ? 'success.main' : 
                                 (product.rotation_rate || 0) > 0.5 ? 'warning.main' : 'error.main'
                        }}
                      >
                        {(product.rotation_rate || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${(product.days_to_sell_inventory || 0).toFixed(0)} días`}
                        size="small"
                        color={
                          (product.days_to_sell_inventory || 0) < 30 ? 'success' : 
                          (product.days_to_sell_inventory || 0) < 60 ? 'warning' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      {getRotationChip(product.rotation_category)}
                    </TableCell>
                    <TableCell align="right">
                      {product.last_sale_date ? 
                        new Date(product.last_sale_date).toLocaleDateString('es-AR') : 
                        'N/A'
                      }
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${product.days_since_last_sale} días`}
                        size="small"
                        color={
                          product.days_since_last_sale < 7 ? 'success' :
                          product.days_since_last_sale < 30 ? 'warning' : 'error'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default RotationAnalysis;
